import { z } from 'zod';
import { retainedDeckSlideSchema, DECK_LIMITS } from '../company-workspace/data/deck-contract.mjs';
import { boundedSchema, id, text, unique } from './models/common.mjs';
import { deckPayloadSchema, propertyPathSchema } from './models/native.mjs';
import { isDeckTextPath, ownedPathValue } from './inspection/deck-paths.mjs';
import { AuthoringError } from './authoring/contract.mjs';

/** Literal slides only. Source slots and managed media require their own
 * current-authorized projection and refuse this entire first Deck display. */
export const savedDeckDisplaySchema = boundedSchema(
  z
    .object({
      kind: z.literal('deck'),
      id,
      title: text(300),
      slides: z
        .array(
          z
            .object({
              id,
              content: retainedDeckSlideSchema,
              nodes: z
                .array(z.object({ id, propertyPath: propertyPathSchema }).strict())
                .min(1)
                .max(200),
            })
            .strict(),
        )
        .min(1)
        .max(DECK_LIMITS.slides),
    })
    .strict()
    .superRefine((value, ctx) => {
      unique([value.id, ...value.slides.flatMap((s) => [s.id, ...s.nodes.map((n) => n.id)])], ctx, [
        'slides',
      ]);
      for (const [index, slide] of value.slides.entries()) {
        unique(
          slide.nodes.map((node) => JSON.stringify(node.propertyPath)),
          ctx,
          ['slides', index, 'nodes'],
        );
        if (
          slide.nodes.some(
            (node) =>
              !isDeckTextPath(slide.content.layout, node.propertyPath) ||
              typeof ownedPathValue(slide.content, node.propertyPath) !== 'string',
          )
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['slides', index, 'nodes'],
            message: 'invalid_deck_text_path',
          });
        }
      }
    }),
);

export function createDeckArtifactDisplayProjector() {
  return Object.freeze({
    kind: 'deck',
    project(_ctx, selected) {
      if (selected.version.version.artifact.kind !== 'deck')
        throw Object.assign(new AuthoringError('artifact_context_mismatch'), { statusCode: 403 });
      const payload = deckPayloadSchema.parse(selected.payload);
      if (payload.slides.some((slide) => slide.nodes.some((node) => node.slot !== undefined)))
        throw Object.assign(new AuthoringError('artifact_view_unavailable'), { statusCode: 503 });
      return savedDeckDisplaySchema.parse({
        kind: 'deck',
        id: payload.id,
        title: payload.title,
        slides: payload.slides.map(({ id, content, nodes }) => ({ id, content, nodes })),
      });
    },
  });
}
